/**
 * Design tokens aligned with Trelleborg brand guidelines.
 *
 * Palette is anchored by the three main brand colors (White, Black, Gold PMS 873)
 * and kept restrained: gold is the sole accent on chrome, status colors draw from
 * the six complementary brand colors in muted form. Typography is Libre Franklin
 * only (the brand digital font) across headline, body, and numeric use.
 *
 * Ported verbatim from the TIS AI Hub reference SPA (reference/web/src/theme.ts)
 * because the brand mapping is the same and was already approved internally.
 */
import type { CSSProperties } from 'react'

export const tokens = {
  // ————— Core palette (brand main + supporting neutrals) —————
  ink: '#111111', // primary text — crisper than pure black, editorial
  ink2: '#3b3b3b', // secondary text
  ink3: '#6b6b6b', // tertiary / meta
  ink4: '#9a9a9a', // muted / placeholder
  paper: '#ffffff', // main background
  paper2: '#f8f6f1', // warm ivory — section shifts
  paper3: '#efebe0', // warmer — hover on neutrals
  line: '#e6e1d4', // primary border (warm light)
  line2: '#d6d0bf', // hover / emphasis border
  line3: '#b9b1a0', // strong divider

  // ————— Gold (brand primary accent, PMS 873 / RGB 153 130 66) —————
  gold: '#998242', // the accent — use sparingly but decisively
  goldSoft: '#b39a5a', // hover / highlight
  goldDark: '#6e5d2c', // text on paper2 where contrast needs a nudge
  goldWash: 'rgba(153,130,66,0.08)',
  goldWashHover: 'rgba(153,130,66,0.14)',
  goldLine: 'rgba(153,130,66,0.3)',

  // ————— Dark "featured" ink panel for hero contrast —————
  inkPanel: '#1a1814', // warm near-black, not slate
  inkPanel2: '#2a2722', // slightly lifted
  inkPanelLine: '#3a362e', // border inside the dark panel
  inkPanelText: '#a8a193', // secondary text on the dark panel (nav, labels, lede)
  inkPanelText2: '#827b6d', // tertiary on dark — legal lines, timestamps
  inkPanelText3: '#d9d3c3', // near-primary on dark — list copy

  // ————— Stage / lifecycle (muted, drawn from brand complementary palette) —————
  // Consumed by StagePill and the funnel board. The ramp runs cool-to-warm
  // along the six funnel stages:
  //   Idea neutral · Shaped orange · Experiment blue · Pilot teal ·
  //   Scaling cyan · Operate green
  // plus two lifecycle treatments that override the stage tone:
  //   blocked amber · closed red
  statusTeal: '#2c7a6e', // Pilot — PMS 5487 family
  statusTealWash: 'rgba(44,122,110,0.09)',
  statusTealLine: 'rgba(44,122,110,0.28)',
  statusBlue: '#1f4e79', // Experiment — PMS 281 family
  statusBlueWash: 'rgba(31,78,121,0.09)',
  statusBlueLine: 'rgba(31,78,121,0.26)',
  statusOrange: '#a55a1f', // Shaped — PMS 145 family
  statusOrangeWash: 'rgba(165,90,31,0.09)',
  statusOrangeLine: 'rgba(165,90,31,0.28)',
  statusRed: '#7a2030', // closed — PMS 704 family
  statusRedDark: '#5c1824',
  statusRedWash: 'rgba(122,32,48,0.08)',
  statusRedLine: 'rgba(122,32,48,0.26)',
  statusGreen: '#4fa646', // Operate — PMS 355
  statusGreenWash: 'rgba(79,166,70,0.09)',
  statusGreenLine: 'rgba(79,166,70,0.28)',
  statusCyan: '#49b1bb', // PMS 3125
  // The PMS 3125 cyan is a display colour; at 13px uppercase on a wash it
  // fails contrast, so text uses this darkened companion.
  statusCyanInk: '#1f6d75', // Scaling
  statusCyanWash: 'rgba(73,177,187,0.11)',
  statusCyanLine: 'rgba(31,109,117,0.28)',
  // Amber is the funnel's attention colour: blocked initiatives, overdue
  // time-boxes, advisory criteria, overrides. Deliberately distinct from
  // the Shaped orange so a blocked Shaped initiative still reads as blocked.
  statusAmber: '#8a6314',
  statusAmberWash: 'rgba(180,132,32,0.13)',
  statusAmberLine: 'rgba(138,99,20,0.32)',

  // Stage marks rendered *on* `inkPanel`. The paper-surface colours above
  // are mixed for white and go muddy against the dark panel, so the home
  // stats band uses these lifted variants of the same brand hues.
  stageOnDark: {
    Idea: '#b8b1a2',
    Shaped: '#d99a5b',
    Experiment: '#8ab4d8',
    Pilot: '#6dc1c9',
    Scaling: '#9fd6db',
    Operate: '#7fc077',
    blocked: '#d9b45b',
    closed: '#d98b96',
  } as Record<string, string>,

  // ————— Typography —————
  fontDisplay: "'Libre Franklin', system-ui, sans-serif",
  fontBody: "'Libre Franklin', system-ui, sans-serif",
  weightBook: 400,
  weightMedium: 500,
  weightDemi: 600,
  weightBold: 700,
  weightHeavy: 800,

  // Type scale (px)
  sizeDisplay1: 'clamp(42px, 6vw, 76px)',
  sizeDisplay2: 'clamp(30px, 4vw, 44px)',
  sizeH1: '32px',
  sizeH2: '24px',
  sizeH3: '18px',
  sizeBodyL: '17px',
  sizeBody: '15px',
  sizeBodyS: '13px',
  sizeMicro: '11px',
  sizeNano: '10px',

  // ————— Spacing / radii —————
  radiusSm: '2px',
  radiusMd: '4px',
  radiusLg: '6px',
  radiusXl: '10px',

  // ————— Motion —————
  easeStandard: 'cubic-bezier(0.22, 1, 0.36, 1)',
  easeSharp: 'cubic-bezier(0.4, 0, 0.2, 1)',
  durFast: '140ms',
  durStd: '260ms',
  durSlow: '520ms',

  // ————— Shells —————
  containerMax: 1280,
  containerPadX: 28,
  topbarH: 36,
  headerH: 68,
} as const

export const microLabel: CSSProperties = {
  fontFamily: tokens.fontDisplay,
  fontSize: tokens.sizeMicro,
  fontWeight: tokens.weightDemi,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: tokens.ink3,
}

export const goldLabel: CSSProperties = {
  ...microLabel,
  color: tokens.gold,
}
